import type {GridColDef} from "@mui/x-data-grid-premium";
import {getGridSingleSelectOperators} from "@mui/x-data-grid-premium";
import {Link} from "@mui/material";
import {generatePath, Link as RouterLink} from "react-router";
import {routes} from "~/routing/routes";
import {CurrencyComponent} from "~/components/currency-component/CurrencyComponent";
import {Tag} from "~/components/tag/Tag";
import type {ContractRow, ContractSummaryResponse} from "~/pages/contract-explorer/contractExplorerTypes";
import {LIFECYCLE_COLOR, LIFECYCLE_LABEL, LIFECYCLES} from "~/pages/contract-explorer/contractExplorerTypes";
import {formatDate, MISSING, typeLabel} from "~/pages/contract-explorer/contractExplorerFormat";

/** The API filters on one value per field, so the pickers offer only equality. */
const IS_ONLY = getGridSingleSelectOperators().filter(op => op.value === 'is');

/** The Contract Explorer with this contract's detail modal open (`?contract=`). */
export function contractExplorerLink(databagId: number, contractId: number): string {
    return `${generatePath(routes.contract_explorer, {id: String(databagId)})}?contract=${contractId}`;
}

function termText(row: ContractRow): string {
    if (!row.starts_on && !row.ends_on) return '';
    return `${formatDate(row.starts_on)} → ${row.ends_on ? formatDate(row.ends_on) : 'open-ended'}`;
}

/**
 * Columns for the Contracts tab, in the design's own order
 * (`suppliers360.jsx::SxContractsTab`: Contract · Type · Value · Term · Status · Open).
 *
 * Fields are the API's own (`GET /data/{databag_id}/contracts/`), so a sort sends the field name
 * as `ordering`. Filtering is Type and Status only, the two the endpoint takes for this tab
 * (`document_type`, `lifecycle`). "Value" is the spend under the contract (`linked_spend`): the
 * contract row carries no contracted amount.
 */
export function getSupplierContractColumns(
    databagId: number,
    typeCounts: ContractSummaryResponse['type_counts'],
): GridColDef<ContractRow>[] {
    return [
        {
            field: 'title',
            headerName: 'Contract',
            flex: 1.8,
            minWidth: 240,
            filterable: false,
            valueGetter: (_value, row) => row.title || row.label,
        },
        {
            field: 'document_type',
            headerName: 'Type',
            width: 180,
            type: 'singleSelect',
            // The databag's actual vocabulary; the value is the slug the API's `document_type` takes.
            valueOptions: typeCounts
                .filter(t => t.document_type)
                .map(t => ({value: t.document_type, label: typeLabel(t.document_type)})),
            filterOperators: IS_ONLY,
            // The cell shows the label, not the option slug, so bypass singleSelect's lookup.
            renderCell: ({row}) => typeLabel(row.document_type),
        },
        {
            field: 'linked_spend',
            headerName: 'Spend under contract',
            type: 'number',
            headerAlign: 'left',
            width: 170,
            filterable: false,
            renderCell: ({value}) => value ? <CurrencyComponent v={Number(value)}/> : MISSING,
        },
        {
            field: 'term',
            headerName: 'Term',
            width: 240,
            sortable: false,
            filterable: false,
            valueGetter: (_value, row) => termText(row),
        },
        {
            field: 'lifecycle',
            headerName: 'Lifecycle',
            width: 130,
            // No `lifecycle` ordering key on the endpoint.
            sortable: false,
            type: 'singleSelect',
            valueOptions: LIFECYCLES.map(value => ({value, label: LIFECYCLE_LABEL[value]})),
            filterOperators: IS_ONLY,
            renderCell: ({row}) => row.lifecycle
                ? <Tag label={LIFECYCLE_LABEL[row.lifecycle]} color={LIFECYCLE_COLOR[row.lifecycle]}/>
                : MISSING,
        },
        {
            field: 'open',
            headerName: '',
            width: 70,
            align: 'right',
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            // `?contract=` opens that contract's detail modal over the explorer list.
            renderCell: ({row}) => (
                <Link
                    component={RouterLink}
                    to={contractExplorerLink(databagId, row.id)}
                    underline="hover"
                    onClick={event => event.stopPropagation()}>
                    Open
                </Link>
            ),
        },
    ];
}
